import React, { useState, useEffect } from 'react';
import api from '../services/api';

const EMITTER = {
  name: 'RMI Electrónica SRL',
  cuit: 'CUIT 30-70821096-6',
  address: 'Luis María Drago 2967, Castelar (CP 1712)',
  phone: 'Tel: 11-52947705',
  email: 'contacto@nubemotic.com.ar'
};

const formatARS = (n) => {
  const num = Number(n) || 0;
  return num.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatDateAR = (d) => {
  if (!d) return '';
  const dt = typeof d === 'string' ? new Date(d) : d;
  return dt.toLocaleDateString('es-AR');
};

const buildReportHTML = ({ client, period, sitesWithValues, ivaRate, usdRate, dueDate, nextDueDate, sitesBilled, logoDataUrl, executiveSummary, chartData, groupBy = 'site', reportType = 'full' }) => {
  const isFull = reportType === 'full';
  const variableCostUSD = sitesWithValues.reduce((acc, s) => acc + (Number(s.value) || 0), 0);
  const subtotalUSD = variableCostUSD;
  const usd = Number(usdRate) || 0;
  const subtotalARS = subtotalUSD * usd;
  const ivaARS = subtotalARS * (ivaRate / 100);
  const totalARS = subtotalARS + ivaARS;

  const issuedAt = new Date().toLocaleDateString('es-AR');

  const groupColLabel = groupBy === 'sector' ? 'Sector' : groupBy === 'device' ? 'Dispositivo' : 'Sitio';
  const groupColLabelPlural = groupBy === 'sector' ? 'Sectores' : groupBy === 'device' ? 'Dispositivos' : 'Sitios';
  const rows = sitesWithValues.map((s, i) => `
    <tr>
      <td>${i + 1}</td>
      <td>${s.name}</td>
      <td class="num">${s.measurementPoints}</td>
      <td class="num">USD ${formatARS(s.value)}</td>
    </tr>
  `).join('');

  return `<!DOCTYPE html>
<html lang="es">
<head>
<meta charset="UTF-8">
<title>Informe de Servicio - ${client.name || ''}</title>
<style>
  * { box-sizing: border-box; }
  body {
    font-family: 'Helvetica Neue', 'Segoe UI', Arial, sans-serif;
    color: #0f172a; margin: 0; padding: 32px 36px; background: #fff;
    font-size: 12px; line-height: 1.45;
  }
  .header {
    display: flex; justify-content: space-between; align-items: flex-start;
    padding-bottom: 18px;
    border-bottom: 3px solid #0c4a6e;
    position: relative;
  }
  .header::after {
    content: ''; position: absolute; left: 0; right: 0; bottom: -6px; height: 3px;
    background: linear-gradient(90deg, #0c4a6e 0%, #0ea5e9 50%, #38bdf8 100%);
  }
  .brand { display: flex; align-items: center; gap: 16px; }
  .brand-logo { width: 64px; height: 64px; object-fit: contain; }
  .brand-fallback {
    width: 64px; height: 64px; border-radius: 50%; background: #0c4a6e;
    display: flex; align-items: center; justify-content: center;
    color: white; font-size: 28px; font-weight: bold; letter-spacing: 1px;
  }
  .brand-text h1 {
    color: #0c4a6e; margin: 0; font-size: 28px; font-weight: 700;
    letter-spacing: 2px;
  }
  .brand-text p {
    margin: 2px 0 0 0; color: #475569; font-size: 11px;
    font-weight: 500; letter-spacing: 0.5px;
  }
  .header-right { text-align: right; }
  .header-right h2 {
    color: #0c4a6e; margin: 0; font-size: 20px;
    font-weight: 700; letter-spacing: 1.5px;
  }
  .header-right .meta { font-size: 10.5px; color: #64748b; margin-top: 3px; }
  .header-right .due {
    margin-top: 8px; padding: 5px 10px;
    background: #0c4a6e; color: white;
    font-weight: 700; font-size: 12px; letter-spacing: 0.5px;
    border-radius: 4px; display: inline-block;
  }

  .blocks { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-top: 28px; }
  .block {
    border: 1px solid #e2e8f0; border-radius: 6px; padding: 14px 18px;
    background: linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
  }
  .block .label {
    color: #0ea5e9; font-size: 10px; font-weight: 700;
    letter-spacing: 1.5px; text-transform: uppercase;
  }
  .block .name { font-size: 15px; font-weight: 700; margin: 8px 0 4px 0; color: #0f172a; }
  .block .line { font-size: 11.5px; color: #475569; line-height: 1.7; }

  h3.section {
    margin: 28px 0 10px 0; font-size: 13px; color: #0c4a6e;
    text-transform: uppercase; letter-spacing: 1.2px; font-weight: 700;
    padding-bottom: 6px; border-bottom: 1px solid #cbd5e1;
  }

  table { width: 100%; border-collapse: collapse; margin-top: 6px; }
  thead th {
    background: #0c4a6e; color: white; text-align: left; padding: 9px 10px;
    font-size: 10.5px; font-weight: 600; letter-spacing: 0.5px;
    text-transform: uppercase;
  }
  thead th:first-child { width: 36px; text-align: center; }
  th.num, td.num { text-align: right; }
  tbody td { padding: 9px 10px; border-bottom: 1px solid #e2e8f0; font-size: 11.5px; }
  tbody td:first-child { color: #94a3b8; text-align: center; font-weight: 600; }
  tbody tr:nth-child(even) td { background: #f8fafc; }
  tbody tr:hover td { background: #eff6ff; }

  .summary { display: grid; grid-template-columns: 1fr 1fr; gap: 18px; margin-top: 24px; align-items: stretch; }
  .summary-left {
    background: #f8fafc; border: 1px solid #e2e8f0;
    border-radius: 6px; padding: 14px 18px; font-size: 11.5px;
  }
  .summary-left h4 {
    margin: 0 0 10px 0; color: #0c4a6e; font-size: 12px;
    text-transform: uppercase; letter-spacing: 1px; font-weight: 700;
  }
  .summary-left .row { display: flex; justify-content: space-between; padding: 4px 0; }
  .summary-left .row b { color: #0f172a; font-weight: 600; }

  .summary-right {
    background: white; border: 1px solid #e2e8f0;
    border-radius: 6px; overflow: hidden;
  }
  .sr-section {
    padding: 12px 18px; background: #fefefe;
    border-bottom: 1px solid #e2e8f0;
  }
  .sr-section.usd { background: #f0f9ff; }
  .sr-section .sr-label {
    font-size: 9.5px; color: #0c4a6e; font-weight: 700;
    letter-spacing: 1.2px; text-transform: uppercase; margin-bottom: 6px;
  }
  .sr-section .row {
    display: flex; justify-content: space-between; padding: 3px 0;
    font-size: 11.5px;
  }
  .sr-section .row.bold { font-weight: 700; padding-top: 6px; margin-top: 4px; border-top: 1px dashed #cbd5e1; }
  .sr-rate {
    padding: 8px 18px; background: #fffbeb; border-bottom: 1px solid #fde68a;
    display: flex; justify-content: space-between; font-size: 11px;
    color: #92400e; font-weight: 600;
  }
  .total {
    background: linear-gradient(135deg, #0c4a6e 0%, #0369a1 100%);
    color: white; padding: 14px 18px;
    display: flex; justify-content: space-between; align-items: center;
    font-weight: 700;
  }
  .total .lbl { font-size: 11px; letter-spacing: 1.5px; text-transform: uppercase; }
  .total .amt { font-size: 18px; }

  .footer {
    margin-top: 36px; padding-top: 14px; border-top: 1px solid #e2e8f0;
    text-align: center; font-size: 9.5px; color: #94a3b8; line-height: 1.7;
  }
  .footer b { color: #0c4a6e; }

  @media print {
    body { padding: 16mm; }
    @page { size: A4; margin: 0; }
  }
  ${EXEC_SUMMARY_CSS}
</style>
</head>
<body>
  <div class="header">
    <div class="brand">
      ${logoDataUrl
        ? `<img src="${logoDataUrl}" alt="NUBEMOTIC" class="brand-logo">`
        : `<div class="brand-fallback">N</div>`}
      <div class="brand-text">
        <h1>NUBEMOTIC</h1>
        <p>Telemetría por Monitoreo Inteligente 24/7</p>
      </div>
    </div>
    <div class="header-right">
      <h2>${isFull ? 'INFORME DE SERVICIO' : 'INFORME TÉCNICO'}</h2>
      <div class="meta">Período: ${formatDateAR(period.from)} — ${formatDateAR(period.to)}</div>
      <div class="meta">Emitido: ${issuedAt}</div>
      ${isFull && dueDate ? `<div class="due">Vencimiento: ${formatDateAR(dueDate)}</div>` : ''}
      ${isFull && nextDueDate ? `<div class="meta" style="margin-top:6px">Próximo vencimiento: ${formatDateAR(nextDueDate)}</div>` : ''}
    </div>
  </div>

  <div class="blocks">
    <div class="block">
      <div class="label">Emisor</div>
      <div class="name">${EMITTER.name}</div>
      <div class="line">${EMITTER.cuit}</div>
      <div class="line">${EMITTER.address}</div>
      <div class="line">${EMITTER.phone}</div>
      <div class="line">${EMITTER.email}</div>
    </div>
    <div class="block">
      <div class="label">Cliente</div>
      <div class="name">${client.name || '-'}</div>
      ${client.cuit ? `<div class="line">CUIT ${client.cuit}</div>` : ''}
      ${client.address ? `<div class="line">${client.address}</div>` : ''}
      ${client.phone ? `<div class="line">Tel: ${client.phone}</div>` : ''}
      ${client.email ? `<div class="line">${client.email}</div>` : ''}
    </div>
  </div>

  ${isFull ? `
  <h3 class="section">${groupColLabelPlural} y puntos de medición</h3>
  <table>
    <thead>
      <tr>
        <th>#</th>
        <th>${groupColLabel}</th>
        <th class="num">Puntos de medición</th>
        <th class="num">Costo (USD)</th>
      </tr>
    </thead>
    <tbody>
      ${rows || `<tr><td colspan="4" style="text-align:center;color:#94a3b8;padding:18px;">Sin ${groupColLabelPlural.toLowerCase()} facturados</td></tr>`}
    </tbody>
  </table>

  <div class="summary">
    <div class="summary-left">
      <h4>Resumen del período</h4>
      <div class="row"><span>${groupColLabelPlural} facturados</span><b>${sitesBilled !== '' && sitesBilled !== null && sitesBilled !== undefined ? sitesBilled : '-'}</b></div>
      <div class="row"><span>Puntos de medición totales</span><b>${sitesWithValues.reduce((a, s) => a + (s.measurementPoints || 0), 0)}</b></div>
      <div class="row"><span>Tipo de cambio aplicado</span><b>$ ${formatARS(usd)} / USD</b></div>
    </div>

    <div class="summary-right">
      <div class="sr-section usd">
        <div class="sr-label">Total en dólares</div>
        <div class="row bold"><span>Subtotal USD</span><span>USD ${formatARS(subtotalUSD)}</span></div>
      </div>
      <div class="sr-rate">
        <span>Cotización USD</span>
        <span>× $ ${formatARS(usd)}</span>
      </div>
      <div class="sr-section">
        <div class="sr-label">Conversión a pesos</div>
        <div class="row"><span>Subtotal</span><span>$ ${formatARS(subtotalARS)}</span></div>
        <div class="row"><span>IVA ${ivaRate}%</span><span>$ ${formatARS(ivaARS)}</span></div>
      </div>
      <div class="total">
        <span class="lbl">Total a pagar</span>
        <span class="amt">$ ${formatARS(totalARS)}</span>
      </div>
    </div>
  </div>

  <div class="footer">
    Este documento es un <b>informe de uso para conciliación</b>. No constituye factura ni tiene validez fiscal.<br>
    <b>NUBEMOTIC</b> · ${EMITTER.email} · ${EMITTER.phone.replace('Tel: ', '')}
  </div>
  ` : `
  <div class="footer" style="margin-top:24px">
    Este documento es un <b>informe técnico de servicio</b> sobre el monitoreo realizado durante el período indicado.<br>
    <b>NUBEMOTIC</b> · ${EMITTER.email} · ${EMITTER.phone.replace('Tel: ', '')}
  </div>
  `}

  ${executiveSummary ? buildExecutiveSummaryHTML(executiveSummary, period.from, period.to, groupBy) : ''}

  ${chartData && Array.isArray(chartData.charts) && chartData.charts.length > 0 ? buildChartsHTML(chartData) : ''}

  <script>
    window.onload = () => { setTimeout(() => window.print(), 400); };
  </script>
</body>
</html>`;
};

// Helpers de formato para resumen ejecutivo
const formatDuration = (ms) => {
  if (!ms || ms <= 0) return '0 min';
  const totalMin = Math.round(ms / 60000);
  if (totalMin < 60) return `${totalMin} min`;
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}min`;
};

const formatDateTimeAR = (d) => {
  if (!d) return '';
  return new Date(d).toLocaleString('es-AR', { hour12: false });
};

const uptimeQualityLabel = (pct) => {
  if (pct >= 99) return { label: 'EXCELENTE', color: '#16a34a', bg: '#dcfce7' };
  if (pct >= 95) return { label: 'BUENA', color: '#0891b2', bg: '#cffafe' };
  if (pct >= 90) return { label: 'REGULAR', color: '#d97706', bg: '#fef3c7' };
  return { label: 'REQUIERE ATENCIÓN', color: '#dc2626', bg: '#fee2e2' };
};

// HTML de las páginas del resumen ejecutivo (se concatena DESPUÉS de la página fiscal)
// groupBy: 'site' (default), 'sector' o 'device'
const buildExecutiveSummaryHTML = (summary, periodFrom, periodTo, groupBy = 'site') => {
  const { kpis, sites, sectors, activityLog } = summary;
  // Para groupBy=device: armamos un único "grupo" sintético con TODOS los devices del cliente
  let groups;
  let groupLabel;
  if (groupBy === 'device') {
    const allDevices = (sites || []).flatMap(s => s.devices || []);
    groups = allDevices.length > 0 ? [{ _id: 'all', name: 'Todos los dispositivos', devices: allDevices }] : [];
    groupLabel = 'Dispositivos';
  } else {
    groups = groupBy === 'sector' ? (sectors || []) : (sites || []);
    groupLabel = groupBy === 'sector' ? 'Sector' : 'Sitio';
  }
  const quality = uptimeQualityLabel(kpis.globalUptimePercent);
  const totalDays = Math.max(1, Math.round((new Date(periodTo) - new Date(periodFrom)) / 86400000) + 1);
  const totalHours = totalDays * 24;

  // Hoja 2: resumen narrativo
  const page2 = `
  <div class="page">
    <h2 class="page-title">Tu servicio en el período</h2>
    <div class="page-subtitle">${formatDateAR(periodFrom)} — ${formatDateAR(periodTo)}</div>

    <div class="hero">
      <div class="hero-pct">${kpis.globalUptimePercent.toFixed(2)}%</div>
      <div class="hero-lbl">Disponibilidad del servicio</div>
      <div class="hero-badge" style="background:${quality.bg};color:${quality.color}">${quality.label}</div>
    </div>

    <p class="narrative">
      Durante este período, tus <b>${kpis.totalDevices} dispositivos</b> distribuidos en
      <b>${kpis.totalSites} ${kpis.totalSites === 1 ? 'sitio' : 'sitios'}</b> fueron monitoreados de forma ininterrumpida 24/7.
      Se detectaron <b>${kpis.totalAlerts} ${kpis.totalAlerts === 1 ? 'evento' : 'eventos'} de alerta</b>
      ${kpis.totalAlerts > 0 ? 'que fueron notificados en tiempo real a tu Telegram. ' : ''}
      ${kpis.totalCommands > 0 ? `Se ejecutaron <b>${kpis.totalCommands} ${kpis.totalCommands === 1 ? 'comando' : 'comandos'}</b> sobre tus dispositivos. ` : ''}
      ${kpis.globalUptimePercent >= 99 ? 'El servicio cumplió con los niveles de disponibilidad esperados.' : 'Algunos dispositivos requieren atención técnica.'}
    </p>

    <div class="kpi-grid">
      <div class="kpi-card">
        <div class="kpi-icon">📡</div>
        <div class="kpi-val">${totalHours}h</div>
        <div class="kpi-lbl">Monitoreo continuo<br><span class="kpi-sub">${totalDays} días × 24h</span></div>
      </div>
      <div class="kpi-card">
        <div class="kpi-icon">🔔</div>
        <div class="kpi-val">${kpis.totalAlerts}</div>
        <div class="kpi-lbl">Eventos detectados<br><span class="kpi-sub">y notificados</span></div>
      </div>
      <div class="kpi-card">
        <div class="kpi-icon">✅</div>
        <div class="kpi-val">${kpis.totalCommands}</div>
        <div class="kpi-lbl">Comandos ejecutados<br><span class="kpi-sub">en tu sistema</span></div>
      </div>
      <div class="kpi-card">
        <div class="kpi-icon">⏱️</div>
        <div class="kpi-val">${formatDuration(kpis.totalOfflineMs)}</div>
        <div class="kpi-lbl">Tiempo total sin reportar<br><span class="kpi-sub">${kpis.totalDisconnections} ${kpis.totalDisconnections === 1 ? 'desconexión' : 'desconexiones'}</span></div>
      </div>
    </div>
  </div>`;

  // Hojas por grupo (sitio o sector)
  const sitePages = groups.filter(g => g.devices.length > 0).map(group => {
    const groupOfflineMs = group.devices.reduce((s, d) => s + d.metrics.uptime.offlineMs, 0);
    const groupAlerts = group.devices.reduce((s, d) => s + d.metrics.alerts.totalAlertCount, 0);
    const groupPeriodMs = (new Date(periodTo) - new Date(periodFrom)) * group.devices.length;
    const groupUptimePct = groupPeriodMs > 0 ? ((groupPeriodMs - groupOfflineMs) / groupPeriodMs) * 100 : 100;

    const deviceCards = group.devices.map(dr => {
      const m = dr.metrics;
      const semaphoreColor = dr.semaphore === 'green' ? '#16a34a' : dr.semaphore === 'yellow' ? '#d97706' : '#dc2626';
      const semaphoreBg = dr.semaphore === 'green' ? '#dcfce7' : dr.semaphore === 'yellow' ? '#fef3c7' : '#fee2e2';
      const semaphoreLbl = dr.semaphore === 'green' ? 'Funcionamiento normal' : dr.semaphore === 'yellow' ? 'Eventos resueltos' : 'Requiere atención';

      // Construir descripción de incidentes
      let incidents = '';
      const magsWithEvents = (m.alerts.magnitudesWithAlerts || []).filter(mag => mag.alertCount > 0);
      if (magsWithEvents.length > 0) {
        incidents += '<div class="incidents-section"><div class="inc-head">Alertas de magnitudes</div><ul>';
        for (const mag of magsWithEvents) {
          const worst = mag.worstAlert;
          incidents += `<li><b>${mag.label}</b> — ${mag.alertCount} ${mag.alertCount === 1 ? 'evento' : 'eventos'} fuera de rango (${formatDuration(mag.totalAlertMs)} acumulado). Mayor incidente: ${formatDateTimeAR(worst.start)}, valor pico ${worst.peakValue != null ? worst.peakValue + (mag.unit || '') : 'N/D'}, durante ${formatDuration(worst.durationMs)}.</li>`;
        }
        incidents += '</ul></div>';
      }
      if (m.uptime.significantOffline.length > 0) {
        incidents += '<div class="incidents-section"><div class="inc-head">Cortes de comunicación significativos</div><ul>';
        for (const off of m.uptime.significantOffline) {
          incidents += `<li>Sin reportar desde ${formatDateTimeAR(off.start)} durante <b>${formatDuration(off.durationMs)}</b>${off.ongoing ? ' <span class="warn">(continúa al cierre del período)</span>' : ' — recuperado'}.</li>`;
        }
        incidents += '</ul></div>';
      }
      if (!incidents) {
        incidents = '<div class="no-incidents">Sin incidentes registrados durante el período.</div>';
      }

      return `
      <div class="device-card">
        <div class="device-header">
          <div class="device-name">${dr.device.name}</div>
          <div class="device-status" style="background:${semaphoreBg};color:${semaphoreColor}">
            <span class="status-dot" style="background:${semaphoreColor}"></span>
            ${semaphoreLbl}
          </div>
        </div>
        <div class="device-kpis">
          <div class="dk"><span class="dk-v">${m.uptime.uptimePercent.toFixed(1)}%</span><span class="dk-l">Disponibilidad</span></div>
          <div class="dk"><span class="dk-v">${formatDuration(m.uptime.offlineMs)}</span><span class="dk-l">Sin reportar</span></div>
          <div class="dk"><span class="dk-v">${m.alerts.totalAlertCount}</span><span class="dk-l">Alertas</span></div>
          <div class="dk"><span class="dk-v">${m.commands.commandCount}</span><span class="dk-l">Comandos</span></div>
        </div>
        ${incidents}
      </div>`;
    }).join('');

    const pageTitle = groupBy === 'device' ? 'Estado de tus dispositivos' : `${groupLabel}: ${group.name}`;
    const summaryLabel = groupBy === 'device' ? 'Disponibilidad promedio' : `Disponibilidad del ${groupLabel.toLowerCase()}`;
    return `
    <div class="page page-break">
      <h2 class="page-title">${pageTitle}</h2>
      <div class="site-summary-bar">
        <div class="ss-item"><span class="ss-v">${groupUptimePct.toFixed(1)}%</span><span class="ss-l">${summaryLabel}</span></div>
        <div class="ss-item"><span class="ss-v">${group.devices.length}</span><span class="ss-l">${group.devices.length === 1 ? 'Dispositivo' : 'Dispositivos'}</span></div>
        <div class="ss-item"><span class="ss-v">${groupAlerts}</span><span class="ss-l">${groupAlerts === 1 ? 'Alerta' : 'Alertas'}</span></div>
      </div>
      ${deviceCards}
    </div>`;
  }).join('');

  // Hoja final: bitácora cronológica
  const activityRows = (activityLog || []).map(ev => {
    const icon = ev.type === 'command' ? '⚙️' : ev.type === 'alert' ? '🔔' : '🔌';
    return `
      <tr>
        <td class="ts">${formatDateTimeAR(ev.timestamp)}</td>
        <td class="ic">${icon}</td>
        <td class="dv">${ev.deviceName}</td>
        <td>${ev.description}</td>
      </tr>`;
  }).join('');

  const activityPage = activityLog && activityLog.length > 0 ? `
  <div class="page page-break">
    <h2 class="page-title">Actividad del mes en tu sistema</h2>
    <div class="page-subtitle">Bitácora cronológica de los eventos más relevantes</div>
    <table class="activity-table">
      <thead>
        <tr><th>Fecha y hora</th><th></th><th>Dispositivo</th><th>Evento</th></tr>
      </thead>
      <tbody>${activityRows}</tbody>
    </table>
    <div class="activity-footer">Mostrando los ${activityLog.length} eventos más relevantes del período. Los microcortes y reconexiones automáticas inferiores a 30 minutos se omiten.</div>
  </div>` : '';

  // Hoja: detalle de eventos por magnitud (alertas + normalizaciones por día)
  const magnitudePages = buildMagnitudeDetailPages(groups, groupBy, periodFrom, periodTo);

  return page2 + sitePages + magnitudePages + activityPage;
};

// Convierte 'YYYY-MM-DD' a 'DD/MM/YYYY'
const formatDayKey = (key) => {
  const [y, m, d] = key.split('-');
  return `${d}/${m}/${y}`;
};

// Genera array de fechas YYYY-MM-DD entre periodFrom y periodTo (inclusive)
const enumeratePeriodDays = (periodFrom, periodTo) => {
  const days = [];
  const start = new Date(periodFrom + 'T00:00:00');
  const end = new Date(periodTo + 'T00:00:00');
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    days.push(`${y}-${m}-${day}`);
  }
  return days;
};

// Determina el "estado" de una magnitud según su nivel de eventos
const magnitudeStatusBadge = (mag) => {
  if (!mag.trackable) return { label: 'NO MONITOREADA', color: '#475569', bg: '#f1f5f9' };
  if (!mag.hasData) return { label: 'SIN DATOS', color: '#475569', bg: '#f1f5f9' };
  if (mag.alertCount === 0) return { label: 'NORMAL', color: '#15803d', bg: '#dcfce7' };
  if (mag.worstAlert?.ongoing) return { label: 'CRÍTICA', color: '#991b1b', bg: '#fecaca' };
  if (mag.alertCount >= 5) return { label: 'CRÍTICA', color: '#991b1b', bg: '#fecaca' };
  if (mag.alertCount >= 3) return { label: 'ALTA', color: '#c2410c', bg: '#fed7aa' };
  return { label: 'MODERADA', color: '#a16207', bg: '#fef3c7' };
};

// Color del cuadrado del heatmap según eventos del día
const heatmapDayColor = (counts, trackable) => {
  if (!trackable) return '#f1f5f9';
  const a = counts?.alerts || 0;
  const n = counts?.normalizations || 0;
  if (a >= 5) return '#991b1b';      // rojo oscuro
  if (a >= 3) return '#dc2626';      // rojo
  if (a >= 1) return '#f59e0b';      // ámbar (alerta)
  if (n >= 1) return '#86efac';      // verde claro (recuperación)
  return '#e2e8f0';                  // gris (sin eventos)
};

// SVG heatmap horizontal de los días del período
const buildHeatmapSVG = (mag, periodDays) => {
  const sqW = 5, sqH = 12, gap = 1;
  const totalW = periodDays.length * (sqW + gap) - gap;
  const rects = periodDays.map((day, i) => {
    const counts = mag.dailyBreakdown?.[day];
    const fill = heatmapDayColor(counts, mag.trackable);
    const x = i * (sqW + gap);
    const tip = `${formatDayKey(day)}: ${counts?.alerts || 0} alertas / ${counts?.normalizations || 0} normalizaciones`;
    return `<rect x="${x}" y="0" width="${sqW}" height="${sqH}" rx="1" fill="${fill}"><title>${tip}</title></rect>`;
  }).join('');
  return `<svg width="${totalW}" height="${sqH}" style="display:block">${rects}</svg>`;
};

// Construye la(s) hoja(s) "Detalle de eventos por magnitud" — diseño compacto + métricas + heatmap
const buildMagnitudeDetailPages = (groups, groupBy, periodFrom, periodTo) => {
  const groupTitleLabel = groupBy === 'sector' ? 'Sector' : groupBy === 'device' ? null : 'Sitio';
  const groupByText = groupBy === 'sector' ? 'sector' : groupBy === 'device' ? 'dispositivo' : 'sitio';
  const periodDays = enumeratePeriodDays(periodFrom, periodTo);
  const numDays = periodDays.length;

  // Etiquetas de eje (1, semanal, último)
  const axisTicks = [];
  axisTicks.push(0);
  if (numDays >= 7) axisTicks.push(Math.floor(numDays / 4));
  if (numDays >= 14) axisTicks.push(Math.floor(numDays / 2));
  if (numDays >= 21) axisTicks.push(Math.floor(numDays * 3 / 4));
  axisTicks.push(numDays - 1);

  const sections = groups
    .filter(g => g.devices.length > 0)
    .map(group => {
      const deviceCards = group.devices.map(dr => {
        const mags = dr.metrics.alerts.magnitudesWithAlerts || [];
        if (mags.length === 0) return '';

        // Stats agregados del dispositivo
        const totalAlerts = mags.reduce((s, m) => s + m.alertCount, 0);
        const totalAlertMs = mags.reduce((s, m) => s + (m.totalAlertMs || 0), 0);
        const trackedCount = mags.filter(m => m.trackable).length;

        const headerStats = totalAlerts > 0
          ? `${totalAlerts} ${totalAlerts === 1 ? 'alerta' : 'alertas'} · ${formatDuration(totalAlertMs)} en alerta`
          : `Sin eventos en el período`;
        const headerStatsColor = totalAlerts > 0 ? '#b45309' : '#15803d';

        const magRows = mags.map(mag => {
          const status = magnitudeStatusBadge(mag);
          const heatmap = buildHeatmapSVG(mag, periodDays);

          // Información de rango/estado
          let rangeText;
          if (mag.valueType === 'boolean') {
            const normalLabel = (mag.normalState === 'true' || mag.normalState === true) ? 'ON' : 'OFF';
            rangeText = `Normal: ${normalLabel}`;
          } else if (mag.minNormalValue != null && mag.maxNormalValue != null) {
            rangeText = `${mag.minNormalValue}–${mag.maxNormalValue}${mag.unit || ''}`;
          } else {
            rangeText = '—';
          }

          // Métricas
          let metricsText;
          if (!mag.trackable) {
            metricsText = '<span style="color:#94a3b8;font-style:italic">No monitoreada</span>';
          } else if (!mag.hasData) {
            metricsText = '<span style="color:#94a3b8;font-style:italic">Sin datos</span>';
          } else if (mag.alertCount === 0) {
            metricsText = '<span style="color:#15803d">Sin eventos</span>';
          } else {
            const peak = mag.worstAlert?.peakValue != null
              ? `<span class="metric-peak"> · Pico: ${mag.worstAlert.peakValue}${mag.unit || ''}</span>` : '';
            metricsText = `<span class="metric-count">${mag.alertCount} ${mag.alertCount === 1 ? 'alerta' : 'alertas'}</span> · <span class="metric-time">${formatDuration(mag.totalAlertMs)}</span>${peak}`;
          }

          return `
            <tr>
              <td class="mst-name">${mag.label}${mag.unit ? `<span class="mst-unit"> ${mag.unit}</span>` : ''}</td>
              <td class="mst-range">${rangeText}</td>
              <td class="mst-status"><span class="status-badge" style="color:${status.color};background:${status.bg}">${status.label}</span></td>
              <td class="mst-metrics">${metricsText}</td>
              <td class="mst-heat">${heatmap}</td>
            </tr>`;
        }).join('');

        return `
          <div class="dev-card-pro">
            <div class="dev-head-pro">
              <div>
                <div class="dev-name-pro">${dr.device.name}</div>
                <div class="dev-meta-pro">${trackedCount}/${mags.length} ${mags.length === 1 ? 'magnitud monitoreada' : 'magnitudes monitoreadas'}</div>
              </div>
              <div class="dev-stats-pro" style="color:${headerStatsColor}">${headerStats}</div>
            </div>
            <table class="mst-table">
              <thead>
                <tr>
                  <th class="mst-name">Magnitud</th>
                  <th class="mst-range">Rango / Normal</th>
                  <th class="mst-status">Estado</th>
                  <th class="mst-metrics">Eventos del período</th>
                  <th class="mst-heat">Distribución diaria · ${numDays} días</th>
                </tr>
              </thead>
              <tbody>${magRows}</tbody>
            </table>
          </div>`;
      }).filter(Boolean).join('');

      if (!deviceCards) return '';

      const groupHeader = groupTitleLabel
        ? `<h3 class="group-section">${groupTitleLabel}: ${group.name}</h3>`
        : '';
      return groupHeader + deviceCards;
    })
    .filter(Boolean)
    .join('');

  if (!sections) {
    return `
      <div class="page page-break">
        <h2 class="page-title">Estado de magnitudes monitoreadas</h2>
        <div class="page-subtitle">Sin magnitudes para reportar en el período</div>
      </div>`;
  }

  // Leyenda de colores del heatmap
  const legend = `
    <div class="heatmap-legend">
      <span class="hl-item"><span class="hl-sq" style="background:#e2e8f0"></span> Sin eventos</span>
      <span class="hl-item"><span class="hl-sq" style="background:#86efac"></span> Solo normalización</span>
      <span class="hl-item"><span class="hl-sq" style="background:#f59e0b"></span> 1–2 alertas</span>
      <span class="hl-item"><span class="hl-sq" style="background:#dc2626"></span> 3–4 alertas</span>
      <span class="hl-item"><span class="hl-sq" style="background:#991b1b"></span> 5+ alertas</span>
      <span class="hl-item"><span class="hl-sq" style="background:#f1f5f9"></span> No monitoreada</span>
    </div>`;

  return `
    <div class="page page-break">
      <h2 class="page-title">Estado de magnitudes monitoreadas</h2>
      <div class="page-subtitle">Resumen por dispositivo, agrupado por ${groupByText} · cada cuadro representa un día del período</div>
      ${legend}
      ${sections}
      <div class="mag-footer">Las alertas de duración inferior a 5 minutos se omiten para evitar ruido. Una alerta cuenta el día que se inició; la normalización el día que volvió al rango.</div>
    </div>`;
};

const EXEC_SUMMARY_CSS = `
  .page { page-break-before: always; padding-top: 8mm; }
  .page-break { page-break-before: always; }
  .page-title { color: #0c4a6e; font-size: 22px; font-weight: 700; margin: 0 0 4px 0; letter-spacing: 0.5px; }
  .page-subtitle { color: #64748b; font-size: 11.5px; margin-bottom: 24px; }

  .hero { text-align: center; padding: 32px 16px 24px; background: linear-gradient(180deg, #f0f9ff 0%, #ffffff 100%); border-radius: 12px; border: 1px solid #bae6fd; margin-bottom: 20px; }
  .hero-pct { font-size: 64px; font-weight: 800; color: #0c4a6e; line-height: 1; letter-spacing: -2px; }
  .hero-lbl { font-size: 13px; color: #475569; font-weight: 600; letter-spacing: 1px; text-transform: uppercase; margin: 6px 0 14px; }
  .hero-badge { display: inline-block; padding: 8px 18px; border-radius: 20px; font-size: 12px; font-weight: 700; letter-spacing: 1.2px; }

  .narrative { font-size: 12.5px; line-height: 1.7; color: #334155; margin: 18px 6px 24px; padding: 14px 18px; background: #fafafa; border-left: 4px solid #0ea5e9; border-radius: 4px; }

  .kpi-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
  .kpi-card { padding: 16px 18px; background: white; border: 1px solid #e2e8f0; border-radius: 8px; display: flex; flex-direction: column; align-items: flex-start; }
  .kpi-icon { font-size: 22px; margin-bottom: 6px; }
  .kpi-val { font-size: 26px; font-weight: 700; color: #0c4a6e; line-height: 1.2; }
  .kpi-lbl { font-size: 11.5px; color: #475569; font-weight: 600; margin-top: 4px; }
  .kpi-sub { font-weight: 400; color: #94a3b8; font-size: 10.5px; }

  .site-summary-bar { display: flex; gap: 14px; padding: 14px 18px; background: #f8fafc; border-left: 4px solid #0c4a6e; border-radius: 4px; margin-bottom: 18px; }
  .ss-item { display: flex; flex-direction: column; }
  .ss-v { font-size: 20px; font-weight: 700; color: #0c4a6e; }
  .ss-l { font-size: 10.5px; color: #64748b; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; }

  .device-card { border: 1px solid #e2e8f0; border-radius: 8px; padding: 14px 16px; margin-bottom: 12px; background: white; page-break-inside: avoid; }
  .device-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; }
  .device-name { font-size: 14px; font-weight: 700; color: #0f172a; }
  .device-status { padding: 4px 10px; border-radius: 12px; font-size: 10.5px; font-weight: 600; display: inline-flex; align-items: center; gap: 6px; }
  .status-dot { width: 8px; height: 8px; border-radius: 50%; display: inline-block; }
  .device-kpis { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; padding: 8px 0 10px; border-bottom: 1px solid #f1f5f9; }
  .dk { display: flex; flex-direction: column; align-items: center; }
  .dk-v { font-size: 16px; font-weight: 700; color: #0c4a6e; }
  .dk-l { font-size: 9.5px; color: #64748b; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; margin-top: 2px; }
  .incidents-section { margin-top: 10px; }
  .inc-head { font-size: 10.5px; color: #0c4a6e; font-weight: 700; text-transform: uppercase; letter-spacing: 0.8px; margin-bottom: 4px; }
  .incidents-section ul { margin: 0; padding-left: 18px; font-size: 11px; line-height: 1.6; color: #334155; }
  .incidents-section li { margin-bottom: 3px; }
  .warn { color: #dc2626; font-weight: 600; }
  .no-incidents { font-size: 11px; color: #16a34a; font-style: italic; padding: 6px 0; }

  .activity-table { width: 100%; border-collapse: collapse; margin-top: 8px; font-size: 11px; }
  .activity-table thead th { background: #0c4a6e; color: white; padding: 8px 10px; text-align: left; font-size: 10.5px; text-transform: uppercase; letter-spacing: 0.5px; }
  .activity-table thead th:nth-child(2) { width: 24px; }
  .activity-table tbody td { padding: 7px 10px; border-bottom: 1px solid #e2e8f0; vertical-align: top; }
  .activity-table tbody td.ts { white-space: nowrap; color: #475569; font-size: 10.5px; }
  .activity-table tbody td.ic { font-size: 14px; text-align: center; }
  .activity-table tbody td.dv { font-weight: 600; color: #0f172a; }
  .activity-table tbody tr:nth-child(even) td { background: #f8fafc; }
  .activity-footer { margin-top: 14px; font-size: 10px; color: #94a3b8; font-style: italic; text-align: center; }

  /* Detalle de eventos por magnitud — diseño compacto con heatmap */
  .group-section { color: #0c4a6e; font-size: 13px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; margin: 18px 0 8px; padding: 5px 10px; background: #f0f9ff; border-left: 4px solid #0ea5e9; border-radius: 0 4px 4px 0; }
  .group-section:first-child { margin-top: 0; }

  .dev-card-pro { border: 1px solid #e2e8f0; border-radius: 8px; padding: 10px 12px; margin-bottom: 10px; background: white; page-break-inside: avoid; }
  .dev-head-pro { display: flex; justify-content: space-between; align-items: center; padding-bottom: 8px; border-bottom: 1px solid #e2e8f0; margin-bottom: 6px; }
  .dev-name-pro { font-size: 12.5px; font-weight: 700; color: #0f172a; }
  .dev-meta-pro { font-size: 10px; color: #64748b; margin-top: 1px; }
  .dev-stats-pro { font-size: 10.5px; font-weight: 600; }

  .mst-table { width: 100%; border-collapse: collapse; font-size: 10.5px; table-layout: fixed; }
  .mst-table thead th { color: #64748b; padding: 4px 6px; text-align: left; font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.6px; border-bottom: 1px solid #e2e8f0; }
  .mst-table tbody td { padding: 6px; border-bottom: 1px solid #f1f5f9; vertical-align: middle; }
  .mst-table tbody tr:last-child td { border-bottom: none; }
  th.mst-name, td.mst-name { width: 22%; font-weight: 600; color: #0f172a; }
  .mst-unit { color: #94a3b8; font-weight: 400; font-size: 9.5px; }
  th.mst-range, td.mst-range { width: 14%; color: #475569; font-size: 10px; }
  th.mst-status, td.mst-status { width: 13%; }
  th.mst-metrics, td.mst-metrics { width: 25%; font-size: 10px; color: #475569; }
  th.mst-heat, td.mst-heat { width: 26%; padding-right: 0; }

  .status-badge { display: inline-block; padding: 2px 7px; border-radius: 9px; font-size: 8.5px; font-weight: 700; letter-spacing: 0.5px; }
  .metric-count { font-weight: 700; color: #0c4a6e; }
  .metric-time { color: #475569; }
  .metric-peak { color: #b45309; font-size: 9.5px; }

  .heatmap-legend { display: flex; flex-wrap: wrap; gap: 10px 14px; padding: 8px 10px; background: #fafafa; border: 1px solid #e2e8f0; border-radius: 6px; margin-bottom: 14px; font-size: 9.5px; color: #475569; }
  .hl-item { display: inline-flex; align-items: center; gap: 4px; }
  .hl-sq { width: 10px; height: 10px; border-radius: 1px; display: inline-block; border: 1px solid rgba(0,0,0,0.05); }

  .mag-footer { margin-top: 16px; font-size: 9px; color: #94a3b8; font-style: italic; text-align: center; }

  /* Gráficos de historial */
  .chart-card { border: 1px solid #e2e8f0; border-radius: 8px; padding: 12px 14px 8px; margin-bottom: 14px; page-break-inside: avoid; background: #fff; }
  .chart-head { display: flex; align-items: baseline; justify-content: space-between; margin-bottom: 6px; border-bottom: 1px solid #f1f5f9; padding-bottom: 5px; }
  .chart-title { font-size: 12px; font-weight: 700; color: #1e293b; }
  .chart-sub { font-size: 9px; color: #94a3b8; }
  .chart-nodata { font-size: 10px; color: #94a3b8; font-style: italic; padding: 18px 0; text-align: center; }
  .bool-legend { display: flex; gap: 14px; font-size: 9px; color: #64748b; margin-top: 4px; }
  .bool-legend span { display: inline-flex; align-items: center; gap: 4px; }
  .bool-legend i { width: 12px; height: 10px; border-radius: 2px; display: inline-block; }
  .chart-alerts { margin-top: 8px; font-size: 9px; color: #7f1d1d; background: #fef2f2; border: 1px solid #fecaca; border-radius: 5px; padding: 6px 8px; line-height: 1.7; }
  .chart-alerts.none { color: #166534; background: #f0fdf4; border-color: #bbf7d0; }
  .chart-alerts .al-item { display: inline-block; margin-right: 10px; white-space: nowrap; }
  .chart-alerts .al-num { display: inline-block; min-width: 12px; height: 12px; line-height: 12px; text-align: center; background: #ef4444; color: #fff; border-radius: 50%; font-weight: bold; font-size: 8px; margin-right: 2px; padding: 0 1px; }
  .chart-alerts .al-more { color: #b91c1c; font-style: italic; }
`;

// ===== Gráficos de historial (SVG inline) =====

// 'YYYY-MM-DD' o 'YYYY-MM-DD HH' -> 'DD/MM' o 'DD/MM HHh'
const shortBucketLabel = (t) => {
  const [datePart, hourPart] = String(t).split(' ');
  const parts = datePart.split('-');
  const d = parts[2], m = parts[1];
  return hourPart != null ? `${d}/${m} ${hourPart}h` : `${d}/${m}`;
};
const formatDayFromMs = (ms) => {
  const dt = new Date(ms);
  const d = String(dt.getDate()).padStart(2, '0');
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  return `${d}/${m}/${dt.getFullYear()}`;
};
// 'DD/MM HH:MM' para etiquetar avisos
const formatDateTimeShort = (ms) => {
  const dt = new Date(ms);
  const d = String(dt.getDate()).padStart(2, '0');
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const hh = String(dt.getHours()).padStart(2, '0');
  const mm = String(dt.getMinutes()).padStart(2, '0');
  return `${d}/${m} ${hh}:${mm}`;
};

// Gráfico de línea para magnitud numérica (promedio por bucket) — eje X temporal, con marcadores de aviso
const buildLineChartSVG = (c, from, to) => {
  const series = c.series || [];
  if (series.length === 0) return '';
  const W = 680, H = 176, padL = 46, padR = 12, padT = 16, padB = 26;
  const plotW = W - padL - padR, plotH = H - padT - padB;
  const vals = series.map(s => s.avg);
  let vMin = Math.min(...vals), vMax = Math.max(...vals);
  const nMin = c.magnitude.minNormalValue, nMax = c.magnitude.maxNormalValue;
  if (typeof nMin === 'number') vMin = Math.min(vMin, nMin);
  if (typeof nMax === 'number') vMax = Math.max(vMax, nMax);
  if (vMin === vMax) { vMin -= 1; vMax += 1; }
  const gap = (vMax - vMin) * 0.08;
  vMin -= gap; vMax += gap;
  const span = Math.max(1, to - from);
  const xt = (ts) => padL + ((ts - from) / span) * plotW;      // eje X por tiempo real
  const y = (v) => padT + plotH - ((v - vMin) / (vMax - vMin)) * plotH;
  const pts = series.map(s => `${xt(s.ts).toFixed(1)},${y(s.avg).toFixed(1)}`).join(' ');
  const yTicks = [vMax - gap, (vMin + vMax) / 2, vMin + gap].map(v =>
    `<line x1="${padL}" y1="${y(v).toFixed(1)}" x2="${(padL + plotW).toFixed(1)}" y2="${y(v).toFixed(1)}" stroke="#f1f5f9" stroke-width="1"/>` +
    `<text x="${padL - 6}" y="${(y(v) + 3).toFixed(1)}" text-anchor="end" font-size="8" fill="#94a3b8">${Math.round(v * 10) / 10}</text>`
  ).join('');
  const rangeLines = [];
  if (typeof nMax === 'number') rangeLines.push(`<line x1="${padL}" y1="${y(nMax).toFixed(1)}" x2="${(padL + plotW).toFixed(1)}" y2="${y(nMax).toFixed(1)}" stroke="#fca5a5" stroke-width="1" stroke-dasharray="4 3"/>`);
  if (typeof nMin === 'number') rangeLines.push(`<line x1="${padL}" y1="${y(nMin).toFixed(1)}" x2="${(padL + plotW).toFixed(1)}" y2="${y(nMin).toFixed(1)}" stroke="#fca5a5" stroke-width="1" stroke-dasharray="4 3"/>`);
  const xLabels = [from, (from + to) / 2, to].map((ts, i) =>
    `<text x="${xt(ts).toFixed(1)}" y="${H - 8}" text-anchor="${i === 0 ? 'start' : i === 2 ? 'end' : 'middle'}" font-size="8" fill="#94a3b8">${formatDayFromMs(ts).slice(0, 5)}</text>`
  ).join('');
  const dots = series.length <= 60 ? series.map(s => `<circle cx="${xt(s.ts).toFixed(1)}" cy="${y(s.avg).toFixed(1)}" r="1.6" fill="#2563eb"/>`).join('') : '';
  // Marcadores de aviso (líneas verticales rojas numeradas)
  const alerts = (c.alerts || []).slice(0, 20);
  const markers = alerts.map((a, i) => {
    const mx = xt(a.start);
    if (mx < padL - 0.5 || mx > padL + plotW + 0.5) return '';
    return `<line x1="${mx.toFixed(1)}" y1="${padT}" x2="${mx.toFixed(1)}" y2="${(padT + plotH).toFixed(1)}" stroke="#ef4444" stroke-width="1" stroke-dasharray="2 2" opacity="0.7"/>` +
      `<circle cx="${mx.toFixed(1)}" cy="${(padT - 4).toFixed(1)}" r="6" fill="#ef4444"/>` +
      `<text x="${mx.toFixed(1)}" y="${(padT - 1.5).toFixed(1)}" text-anchor="middle" font-size="7.5" fill="#fff" font-weight="bold">${i + 1}</text>`;
  }).join('');
  return `<svg width="100%" viewBox="0 0 ${W} ${H}" preserveAspectRatio="xMidYMid meet" style="display:block">
    ${yTicks}${rangeLines.join('')}
    <line x1="${padL}" y1="${padT}" x2="${padL}" y2="${(padT + plotH).toFixed(1)}" stroke="#cbd5e1" stroke-width="1"/>
    <line x1="${padL}" y1="${(padT + plotH).toFixed(1)}" x2="${(padL + plotW).toFixed(1)}" y2="${(padT + plotH).toFixed(1)}" stroke="#cbd5e1" stroke-width="1"/>
    <polyline points="${pts}" fill="none" stroke="#2563eb" stroke-width="1.6" stroke-linejoin="round" stroke-linecap="round"/>
    ${dots}${markers}${xLabels}
  </svg>`;
};

// Timeline ON/OFF para magnitud booleana — con marcadores de aviso numerados
const buildBooleanTimelineSVG = (c, from, to) => {
  const intervals = c.intervals || [];
  const W = 680, H = 54, padL = 8, padR = 8, padT = 16, barH = 22;
  const plotW = W - padL - padR;
  const span = Math.max(1, to - from);
  const xt = (ts) => padL + ((ts - from) / span) * plotW;
  const segs = intervals.map(iv => {
    const x0 = xt(iv.start);
    const w = Math.max(0.4, ((iv.end - iv.start) / span) * plotW);
    const fill = iv.on ? '#16a34a' : '#cbd5e1';
    return `<rect x="${x0.toFixed(1)}" y="${padT}" width="${w.toFixed(1)}" height="${barH}" fill="${fill}"><title>${iv.on ? 'ON' : 'OFF'}</title></rect>`;
  }).join('');
  const alerts = (c.alerts || []).slice(0, 20);
  const markers = alerts.map((a, i) => {
    const mx = xt(a.start);
    return `<circle cx="${mx.toFixed(1)}" cy="${(padT - 5).toFixed(1)}" r="6" fill="#ef4444"/>` +
      `<text x="${mx.toFixed(1)}" y="${(padT - 2.5).toFixed(1)}" text-anchor="middle" font-size="7.5" fill="#fff" font-weight="bold">${i + 1}</text>` +
      `<line x1="${mx.toFixed(1)}" y1="${padT}" x2="${mx.toFixed(1)}" y2="${padT + barH}" stroke="#ef4444" stroke-width="1" opacity="0.55"/>`;
  }).join('');
  return `<svg width="100%" viewBox="0 0 ${W} ${H}" preserveAspectRatio="xMidYMid meet" style="display:block">
    <rect x="${padL}" y="${padT}" width="${plotW}" height="${barH}" fill="#f8fafc" stroke="#e2e8f0"/>
    ${segs}${markers}
    <text x="${padL}" y="${padT + barH + 11}" font-size="8" fill="#94a3b8">${formatDayFromMs(from)}</text>
    <text x="${padL + plotW}" y="${padT + barH + 11}" text-anchor="end" font-size="8" fill="#94a3b8">${formatDayFromMs(to)}</text>
  </svg>
  <div class="bool-legend"><span><i style="background:#16a34a"></i>ON</span><span><i style="background:#cbd5e1"></i>OFF</span></div>`;
};

// Hoja de gráficos de historial: un gráfico separado por magnitud seleccionada
const buildChartsHTML = (chartData) => {
  const charts = chartData.charts || [];
  const bucketLbl = chartData.bucket === 'hour' ? 'por hora' : 'por día';
  const cards = charts.map(c => {
    const isBool = c.magnitude.valueType === 'boolean';
    const sub = isBool
      ? `Estado ON/OFF · ${c.deviceName}`
      : `${c.bucket === 'hour' ? 'Promedio por hora' : 'Promedio diario'} · ${c.deviceName}${c.magnitude.unit ? ` · ${c.magnitude.unit}` : ''}`;
    const body = !c.hasData
      ? '<div class="chart-nodata">Sin datos en el período seleccionado</div>'
      : (isBool ? buildBooleanTimelineSVG(c, chartData.from, chartData.to) : buildLineChartSVG(c, chartData.from, chartData.to));
    // Leyenda de avisos con fecha y hora
    const alerts = c.alerts || [];
    let alertsCaption;
    if (!c.hasData) {
      alertsCaption = '';
    } else if (alerts.length === 0) {
      alertsCaption = '<div class="chart-alerts none">✓ Sin avisos fuera de rango en el período</div>';
    } else {
      const shown = alerts.slice(0, 20);
      const items = shown.map((a, i) =>
        `<span class="al-item"><span class="al-num">${i + 1}</span> ${formatDateTimeShort(a.start)}` +
        `${(a.peakValue != null && !isBool) ? ` · pico ${a.peakValue}${c.magnitude.unit || ''}` : ''}` +
        `${a.durationMs ? ` · ${formatDuration(a.durationMs)}` : ''}${a.ongoing ? ' (sigue)' : ''}</span>`
      ).join('');
      const more = alerts.length > shown.length ? `<span class="al-more">…y ${alerts.length - shown.length} más</span>` : '';
      alertsCaption = `<div class="chart-alerts"><b>⚠ ${alerts.length} aviso${alerts.length === 1 ? '' : 's'}:</b> ${items} ${more}</div>`;
    }
    return `
      <div class="chart-card">
        <div class="chart-head">
          <div class="chart-title">${c.magnitude.label}</div>
          <div class="chart-sub">${sub}</div>
        </div>
        ${body}
        ${alertsCaption}
      </div>`;
  }).join('');
  return `
  <div class="page page-break">
    <h2 class="page-title">Gráficos de historial</h2>
    <div class="page-subtitle">Evolución de las magnitudes seleccionadas — muestreo ${bucketLbl}</div>
    ${cards}
  </div>`;
};

// Rechaza si la promesa no resuelve dentro de `ms` — evita que la generación quede colgada para siempre.
const withTimeout = (promise, ms, label) => Promise.race([
  promise,
  new Promise((_, reject) => setTimeout(
    () => reject(new Error((label || 'La operación') + ' tardó demasiado (más de ' + Math.round(ms / 1000) + 's). Reintentá o achicá el período.')),
    ms
  ))
]);

// Convertir el logo (servido por la app) a Data URL para embeberlo en la nueva ventana
const fetchLogoAsDataURL = async (path) => {
  try {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 10000);
    const r = await fetch(path, { signal: ctrl.signal });
    clearTimeout(timer);
    if (!r.ok) return null;
    const blob = await r.blob();
    return await new Promise(resolve => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch (_) { return null; }
};

const ClientsManagement = ({ user }) => {
  const [clients, setClients] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportClient, setReportClient] = useState(null);

  const [users, setUsers] = useState([]);
  const emptyForm = { name: '', cuit: '', address: '', phone: '', email: '', notes: '', sites: [], users: [] };
  const [form, setForm] = useState(emptyForm);

  const loadData = async () => {
    try {
      const [c, s, u] = await Promise.all([
        api.get('/api/clients'),
        api.get('/api/sites'),
        api.get('/api/users')
      ]);
      setClients(c.data || []);
      setSites(s.data || []);
      setUsers(u.data || []);
      setError(null);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const openNewForm = () => {
    setEditingClient(null);
    setForm(emptyForm);
    setShowForm(true);
  };

  const openEditForm = async (client) => {
    try {
      // Cargar el cliente completo con sus sites/users actuales
      const r = await api.get(`/api/clients/${client._id}`);
      const full = r.data;
      setEditingClient(full);
      setForm({
        name: full.name || '',
        cuit: full.cuit || '',
        address: full.address || '',
        phone: full.phone || '',
        email: full.email || '',
        notes: full.notes || '',
        sites: (full.sites || []).map(s => s._id || s),
        users: (full.users || []).map(u => u._id || u)
      });
      setShowForm(true);
    } catch (e) {
      alert('Error al cargar cliente: ' + (e.response?.data?.message || e.message));
    }
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      alert('El nombre del cliente es obligatorio');
      return;
    }
    try {
      if (editingClient) {
        await api.put(`/api/clients/${editingClient._id}`, form);
      } else {
        await api.post('/api/clients', form);
      }
      setShowForm(false);
      loadData();
    } catch (e) {
      alert('Error: ' + (e.response?.data?.message || e.message));
    }
  };

  const handleDelete = async (client) => {
    if (!window.confirm(`¿Eliminar cliente "${client.name}"?`)) return;
    try {
      await api.delete(`/api/clients/${client._id}`);
      loadData();
    } catch (e) {
      alert('Error: ' + (e.response?.data?.message || e.message));
    }
  };

  const toggleSite = (siteId) => {
    setForm(f => ({
      ...f,
      sites: f.sites.includes(siteId) ? f.sites.filter(id => id !== siteId) : [...f.sites, siteId]
    }));
  };

  const toggleUser = (userId) => {
    setForm(f => ({
      ...f,
      users: f.users.includes(userId) ? f.users.filter(id => id !== userId) : [...f.users, userId]
    }));
  };

  // Determinar qué sitios/usuarios están ocupados por OTROS clientes (no editables aquí)
  const sitesAssignedElsewhere = new Set();
  const usersAssignedElsewhere = new Set();
  if (editingClient) {
    sites.forEach(s => {
      if (s.client && String(s.client) !== String(editingClient._id) && !form.sites.includes(s._id)) {
        sitesAssignedElsewhere.add(s._id);
      }
    });
    users.forEach(u => {
      if (u.client && String(u.client) !== String(editingClient._id) && !form.users.includes(u._id)) {
        usersAssignedElsewhere.add(u._id);
      }
    });
  } else {
    sites.forEach(s => { if (s.client) sitesAssignedElsewhere.add(s._id); });
    users.forEach(u => { if (u.client) usersAssignedElsewhere.add(u._id); });
  }

  if (user?.role !== 'admin') {
    return <div style={{ padding: '2rem', textAlign: 'center', color: '#ef4444' }}>Acceso denegado. Esta página es solo para administradores.</div>;
  }

  return (
    <div style={{ padding: '1.5rem', maxWidth: '1400px', margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <div>
          <h2 style={{ margin: 0, color: '#1f2937' }}>💼 Clientes & Informes</h2>
          <p style={{ margin: '0.25rem 0 0 0', color: '#6b7280', fontSize: '0.875rem' }}>
            Gestión de clientes y generación de informes de servicio en PDF.
          </p>
        </div>
        <button onClick={openNewForm} style={{
          padding: '0.6rem 1.1rem', background: '#10b981', color: 'white', border: 'none',
          borderRadius: '6px', cursor: 'pointer', fontSize: '0.875rem', fontWeight: 600
        }}>+ Nuevo cliente</button>
      </div>

      {loading && <div style={{ color: '#6b7280' }}>Cargando…</div>}
      {error && <div style={{ color: '#ef4444', padding: '0.75rem', background: '#fee2e2', borderRadius: '8px' }}>{error}</div>}

      {!loading && !error && clients.length === 0 && (
        <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280', background: 'white', borderRadius: '8px' }}>
          No hay clientes registrados. Crea el primero con "+ Nuevo cliente".
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))', gap: '1rem' }}>
        {clients.map(c => (
          <div key={c._id} style={{
            background: 'white', borderRadius: '12px', border: '1px solid #e5e7eb',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)', padding: '1rem'
          }}>
            <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.05rem', color: '#1f2937' }}>{c.name}</h3>
            <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '0.6rem', flexWrap: 'wrap' }}>
              <Badge color="#1e40af" bg="#dbeafe">{c.siteCount || 0} sitios</Badge>
              <Badge color="#7e22ce" bg="#f3e8ff">{c.userCount || 0} usuarios</Badge>
              <Badge color="#15803d" bg="#dcfce7">{c.deviceCount || 0} dispositivos</Badge>
            </div>
            <div style={{ fontSize: '0.8rem', color: '#6b7280', lineHeight: 1.5 }}>
              {c.cuit && <div>CUIT {c.cuit}</div>}
              {c.address && <div>{c.address}</div>}
              {c.email && <div>{c.email}</div>}
              {c.phone && <div>Tel: {c.phone}</div>}
            </div>
            <div style={{ display: 'flex', gap: '0.4rem', marginTop: '0.85rem' }}>
              <button onClick={() => { setReportClient(c); setShowReportModal(true); }} style={{
                flex: 1, padding: '0.5rem', background: '#0ea5e9', color: 'white', border: 'none',
                borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600
              }}>📄 Generar Informe</button>
              <button onClick={() => openEditForm(c)} style={{
                padding: '0.5rem 0.7rem', background: '#f3f4f6', color: '#1f2937', border: '1px solid #d1d5db',
                borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem'
              }}>✏️</button>
              <button onClick={() => handleDelete(c)} style={{
                padding: '0.5rem 0.7rem', background: '#fee2e2', color: '#dc2626', border: '1px solid #fca5a5',
                borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem'
              }}>🗑️</button>
            </div>
          </div>
        ))}
      </div>

      {showForm && (
        <ClientForm
          form={form}
          setForm={setForm}
          sites={sites}
          users={users}
          toggleSite={toggleSite}
          toggleUser={toggleUser}
          sitesAssignedElsewhere={sitesAssignedElsewhere}
          usersAssignedElsewhere={usersAssignedElsewhere}
          onSave={handleSave}
          onCancel={() => setShowForm(false)}
          isEditing={!!editingClient}
        />
      )}

      {showReportModal && reportClient && (
        <ReportModal
          client={reportClient}
          onClose={() => { setShowReportModal(false); setReportClient(null); }}
        />
      )}
    </div>
  );
};

const ClientForm = ({ form, setForm, sites, users, toggleSite, toggleUser, sitesAssignedElsewhere, usersAssignedElsewhere, onSave, onCancel, isEditing }) => (
  <div style={modalBackdrop} onClick={onCancel}>
    <div style={{ ...modalBox, maxWidth: '700px' }} onClick={e => e.stopPropagation()}>
      <h3 style={{ marginTop: 0 }}>{isEditing ? 'Editar cliente' : 'Nuevo cliente'}</h3>
      <div style={{ display: 'grid', gap: '0.75rem' }}>
        <Field label="Nombre / Razón social *" value={form.name} onChange={v => setForm({ ...form, name: v })} />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
          <Field label="CUIT" value={form.cuit} onChange={v => setForm({ ...form, cuit: v })} placeholder="30-12345678-9" />
          <Field label="Teléfono" value={form.phone} onChange={v => setForm({ ...form, phone: v })} />
        </div>
        <Field label="Dirección" value={form.address} onChange={v => setForm({ ...form, address: v })} />
        <Field label="Email" value={form.email} onChange={v => setForm({ ...form, email: v })} type="email" />
        <Field label="Notas" value={form.notes} onChange={v => setForm({ ...form, notes: v })} multiline />

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
          <div>
            <label style={{ fontSize: '0.85rem', color: '#374151', fontWeight: 600 }}>Sitios</label>
            <div style={{ marginTop: '0.4rem', maxHeight: '200px', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '0.5rem' }}>
              {sites.length === 0 && <div style={{ color: '#9ca3af', fontSize: '0.85rem' }}>Sin sitios</div>}
              {sites.map(s => {
                const taken = sitesAssignedElsewhere.has(s._id);
                return (
                  <label key={s._id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.25rem', cursor: taken ? 'not-allowed' : 'pointer', opacity: taken ? 0.5 : 1 }}>
                    <input type="checkbox" disabled={taken} checked={form.sites.includes(s._id)} onChange={() => toggleSite(s._id)} />
                    <span style={{ fontSize: '0.85rem' }}>
                      {s.name}
                      {taken && <span style={{ fontSize: '0.7rem', color: '#dc2626', marginLeft: '0.4rem' }}>(otro cliente)</span>}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
          <div>
            <label style={{ fontSize: '0.85rem', color: '#374151', fontWeight: 600 }}>Usuarios</label>
            <div style={{ marginTop: '0.4rem', maxHeight: '200px', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '0.5rem' }}>
              {users.length === 0 && <div style={{ color: '#9ca3af', fontSize: '0.85rem' }}>Sin usuarios</div>}
              {users.map(u => {
                const taken = usersAssignedElsewhere.has(u._id);
                return (
                  <label key={u._id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.25rem', cursor: taken ? 'not-allowed' : 'pointer', opacity: taken ? 0.5 : 1 }}>
                    <input type="checkbox" disabled={taken} checked={form.users.includes(u._id)} onChange={() => toggleUser(u._id)} />
                    <span style={{ fontSize: '0.85rem' }}>
                      {u.username} <span style={{ color: '#9ca3af' }}>({u.role})</span>
                      {taken && <span style={{ fontSize: '0.7rem', color: '#dc2626', marginLeft: '0.4rem' }}>(otro cliente)</span>}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
        </div>
        <div style={{ fontSize: '0.75rem', color: '#6b7280', fontStyle: 'italic' }}>
          ℹ️ Los dispositivos quedan asociados automáticamente al cliente a través de su sitio.
        </div>
      </div>
      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1.25rem', justifyContent: 'flex-end' }}>
        <button onClick={onCancel} style={btnSecondary}>Cancelar</button>
        <button onClick={onSave} style={btnPrimary}>{isEditing ? 'Guardar cambios' : 'Crear cliente'}</button>
      </div>
    </div>
  </div>
);

const Badge = ({ color, bg, children }) => (
  <span style={{ fontSize: '0.7rem', padding: '0.2rem 0.5rem', borderRadius: '4px', background: bg, color, fontWeight: 600 }}>
    {children}
  </span>
);

const Field = ({ label, value, onChange, placeholder, type = 'text', multiline }) => {
  const handleDateClick = (e) => {
    if (type === 'date' && e.target.showPicker) {
      try { e.target.showPicker(); } catch (_) {}
    }
  };
  return (
    <div>
      <label style={{ display: 'block', fontSize: '0.8rem', color: '#374151', fontWeight: 600, marginBottom: '0.25rem' }}>{label}</label>
      {multiline ? (
        <textarea value={value} onChange={e => onChange(e.target.value)} rows={2}
          style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.85rem', fontFamily: 'inherit' }} />
      ) : (
        <input
          type={type}
          value={value}
          onChange={e => onChange(e.target.value)}
          onClick={handleDateClick}
          onFocus={handleDateClick}
          placeholder={placeholder}
          style={{
            width: '100%',
            padding: '0.5rem',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            fontSize: '0.85rem',
            cursor: type === 'date' ? 'pointer' : 'text'
          }}
        />
      )}
    </div>
  );
};

const ReportModal = ({ client, onClose }) => {
  const today = new Date();
  // Período = mes anterior facturado
  const billedMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  const billedMonthLastDay = new Date(today.getFullYear(), today.getMonth(), 0);
  // Vencimiento = día 10 del mes actual (próximo al período facturado)
  const dueOfMonth = new Date(today.getFullYear(), today.getMonth(), 10);
  // Próximo vencimiento = día 10 del mes siguiente
  const nextDueOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 10);

  const isoDate = (d) => d.toISOString().slice(0, 10);

  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [periodFrom, setPeriodFrom] = useState(isoDate(billedMonth));
  const [periodTo, setPeriodTo] = useState(isoDate(billedMonthLastDay));
  const [dueDate, setDueDate] = useState(isoDate(dueOfMonth));
  const [nextDueDate, setNextDueDate] = useState(isoDate(nextDueOfMonth));
  const [ivaRate, setIvaRate] = useState(21);
  const [usdRate, setUsdRate] = useState('');
  const [sitesBilled, setSitesBilled] = useState('');
  const [siteValues, setSiteValues] = useState({});
  const [includeExecSummary, setIncludeExecSummary] = useState(true);
  const [includeCharts, setIncludeCharts] = useState(false);
  const [chartSelections, setChartSelections] = useState({}); // { [deviceId]: [magName, ...] }

  const toggleChartMag = (deviceId, magName) => {
    setChartSelections(prev => {
      const cur = prev[deviceId] || [];
      const next = cur.includes(magName) ? cur.filter(m => m !== magName) : [...cur, magName];
      const out = { ...prev, [deviceId]: next };
      if (next.length === 0) delete out[deviceId];
      return out;
    });
  };
  const [groupBy, setGroupBy] = useState('site'); // 'site' | 'sector' | 'device'
  const [reportType, setReportType] = useState('full'); // 'full' (informe + facturación) | 'summary' (solo informe)
  const [generating, setGenerating] = useState(false);

  // Recalcular vencimiento cuando cambia el período: día 10 del mes posterior al "hasta"
  useEffect(() => {
    if (!periodTo) return;
    const to = new Date(periodTo + 'T00:00:00');
    if (isNaN(to.getTime())) return;
    const due = new Date(to.getFullYear(), to.getMonth() + 1, 10);
    const next = new Date(to.getFullYear(), to.getMonth() + 2, 10);
    setDueDate(isoDate(due));
    setNextDueDate(isoDate(next));
  }, [periodTo]);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/api/clients/${client._id}/report-data`);
        setReportData(r.data);
        const initial = {};
        (r.data.sites || []).forEach(s => { initial[s._id] = ''; });
        setSiteValues(initial);
      } catch (e) {
        alert('Error al cargar datos: ' + (e.response?.data?.message || e.message));
      } finally {
        setLoading(false);
      }
    })();
  }, [client._id]);

  const handleGenerate = async () => {
    if (!reportData) return;
    // Cotización solo es obligatoria si se genera la hoja de facturación
    if (reportType === 'full' && (!usdRate || parseFloat(usdRate) <= 0)) {
      alert('Indicar la cotización del dólar para generar el informe con facturación.');
      return;
    }

    // IMPORTANTE: abrir la ventana AHORA, dentro del gesto del click. Si se abre después
    // de los await (fetch del resumen ~8s), el navegador la bloquea por no haber gesto de usuario.
    const reportWindow = window.open('', '_blank', 'width=1000,height=900,scrollbars=yes');
    if (!reportWindow) {
      alert('El navegador bloqueó la ventana emergente. Habilitá los pop-ups para este sitio (ícono en la barra de direcciones) y volvé a generar.');
      return;
    }
    reportWindow.document.write('<!doctype html><html><head><meta charset="utf-8"><title>Generando informe…</title></head><body style="margin:0;height:100vh;display:flex;align-items:center;justify-content:center;font-family:system-ui,-apple-system,sans-serif;color:#334155"><div style="text-align:center"><div style="font-size:44px">⏳</div><div style="font-size:18px;margin-top:12px">Generando informe…</div><div style="font-size:13px;color:#94a3b8;margin-top:6px">Esto puede tardar unos segundos.</div></div></body></html>');
    reportWindow.document.close();

    setGenerating(true);
    try {
      const groupList = groupBy === 'sector' ? (reportData.sectors || [])
                       : groupBy === 'device' ? (reportData.devices || [])
                       : (reportData.sites || []);
      const sitesWithValues = groupList.map(g => ({
        ...g,
        value: parseFloat(siteValues[g._id]) || 0
      }));

      // En modo "solo informe" el resumen ejecutivo es obligatorio (es el contenido del PDF)
      const wantSummary = reportType === 'summary' || includeExecSummary;
      let executiveSummary = null;
      if (wantSummary) {
        // Timeout de seguridad: si el request nunca resuelve, cortamos en 120s en vez de colgar para siempre.
        const r = await withTimeout(
          api.get(`/api/clients/${client._id}/report-summary`, { from: periodFrom, to: periodTo, groupBy }),
          120000, 'El resumen ejecutivo'
        );
        executiveSummary = r.data;
      }

      // Gráficos de historial (opcional): traer las series de las magnitudes tildadas
      let chartData = null;
      if (includeCharts) {
        const selections = Object.entries(chartSelections)
          .flatMap(([deviceId, mags]) => mags.map(magnitude => ({ deviceId, magnitude })));
        if (selections.length > 0) {
          const cr = await withTimeout(
            api.post(`/api/clients/${client._id}/report-charts`, { from: periodFrom, to: periodTo, selections }),
            120000, 'Los gráficos de historial'
          );
          chartData = cr.data;
        }
      }

      const logoDataUrl = await fetchLogoAsDataURL('/nubemotic.png');
      const html = buildReportHTML({
        client: reportData.client,
        period: { from: periodFrom, to: periodTo },
        sitesWithValues,
        ivaRate: parseFloat(ivaRate) || 0,
        usdRate: parseFloat(usdRate) || 0,
        dueDate,
        nextDueDate,
        sitesBilled,
        logoDataUrl,
        executiveSummary,
        chartData,
        groupBy,
        reportType
      });
      // Escribir el informe en la ventana ya abierta
      reportWindow.document.open();
      reportWindow.document.write(html);
      reportWindow.document.close();
    } catch (e) {
      const msg = e.response?.data?.message || e.message || 'Error desconocido';
      try {
        reportWindow.document.open();
        reportWindow.document.write('<!doctype html><body style="margin:0;padding:48px;font-family:system-ui,-apple-system,sans-serif;color:#b91c1c"><h2>No se pudo generar el informe</h2><p style="color:#334155">' + msg + '</p><p style="color:#64748b;font-size:13px">Cerrá esta ventana e intentá nuevamente.</p></body>');
        reportWindow.document.close();
      } catch (_) { /* la ventana pudo haberse cerrado */ }
      alert('No se pudo generar el informe: ' + msg);
    } finally {
      setGenerating(false);
    }
  };

  const variableCostUSD = reportData ? reportData.sites.reduce((a, s) => a + (parseFloat(siteValues[s._id]) || 0), 0) : 0;
  const subtotalUSD = variableCostUSD;
  const usd = parseFloat(usdRate) || 0;
  const subtotalARS = subtotalUSD * usd;
  const ivaARS = subtotalARS * ((parseFloat(ivaRate) || 0) / 100);
  const totalARS = subtotalARS + ivaARS;

  return (
    <div style={modalBackdrop} onClick={onClose}>
      <div style={{ ...modalBox, maxWidth: '760px' }} onClick={e => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>📄 Generar informe — {client.name}</h3>

        {loading && <div style={{ color: '#6b7280' }}>Cargando…</div>}

        {!loading && reportData && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '0.7rem 0.95rem', background: '#ecfdf5', border: '1px solid #6ee7b7', borderRadius: '6px', marginBottom: '0.75rem', fontSize: '0.9rem', color: '#065f46', flexWrap: 'wrap' }}>
              <b>Tipo de informe:</b>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', cursor: 'pointer' }}>
                <input type="radio" name="reportType" value="full" checked={reportType === 'full'} onChange={() => setReportType('full')} />
                Informe + Facturación
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', cursor: 'pointer' }}>
                <input type="radio" name="reportType" value="summary" checked={reportType === 'summary'} onChange={() => setReportType('summary')} />
                Solo Informe
              </label>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: reportType === 'summary' ? '1fr 1fr' : '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
              <Field label="Período desde" type="date" value={periodFrom} onChange={setPeriodFrom} />
              <Field label="Período hasta" type="date" value={periodTo} onChange={setPeriodTo} />
              {reportType === 'full' && <Field label="Vencimiento (día 10)" type="date" value={dueDate} onChange={setDueDate} />}
              {reportType === 'full' && <Field label="Próximo vencimiento" type="date" value={nextDueDate} onChange={setNextDueDate} />}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '0.6rem 0.85rem', background: '#fefce8', border: '1px solid #fde68a', borderRadius: '6px', marginBottom: '0.75rem', fontSize: '0.85rem', color: '#713f12', flexWrap: 'wrap' }}>
              <b>Clasificar por:</b>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', cursor: 'pointer' }}>
                <input type="radio" name="groupByMain" value="site" checked={groupBy === 'site'} onChange={() => setGroupBy('site')} />
                Sitio
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', cursor: 'pointer' }}>
                <input type="radio" name="groupByMain" value="sector" checked={groupBy === 'sector'} onChange={() => setGroupBy('sector')} />
                Sector
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', cursor: 'pointer' }}>
                <input type="radio" name="groupByMain" value="device" checked={groupBy === 'device'} onChange={() => setGroupBy('device')} />
                Dispositivo
              </label>
              <span style={{ width: '100%', fontSize: '0.75rem', color: '#92400e', fontStyle: 'italic' }}>
                Afecta tabla de facturación y resumen ejecutivo. El precio se ingresa por cada {groupBy === 'sector' ? 'sector' : groupBy === 'device' ? 'dispositivo' : 'sitio'}.
              </span>
            </div>

            {reportType === 'full' && (() => {
              const groupList = groupBy === 'sector' ? (reportData.sectors || [])
                              : groupBy === 'device' ? (reportData.devices || [])
                              : (reportData.sites || []);
              const groupLabel = groupBy === 'sector' ? 'Sector' : groupBy === 'device' ? 'Dispositivo' : 'Sitio';
              const groupLabelPlural = groupBy === 'sector' ? 'Sectores' : groupBy === 'device' ? 'Dispositivos' : 'Sitios';
              const groupLabelLower = groupBy === 'sector' ? 'sector' : groupBy === 'device' ? 'dispositivo' : 'sitio';
              const emptyMsg = groupBy === 'sector'
                ? 'Cliente sin sectores creados. Crear sectores desde Configuración → Sectores.'
                : groupBy === 'device'
                ? 'Cliente sin dispositivos asignados.'
                : 'Cliente sin sitios asignados.';
              return (
                <div style={{ marginBottom: '0.75rem' }}>
                  <label style={{ fontSize: '0.85rem', color: '#374151', fontWeight: 600 }}>{groupLabelPlural} — valor en USD por {groupLabelLower}</label>
                  <div style={{ marginTop: '0.4rem', border: '1px solid #e5e7eb', borderRadius: '6px', overflow: 'hidden', maxHeight: '320px', overflowY: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                      <thead style={{ background: '#f3f4f6', position: 'sticky', top: 0 }}>
                        <tr>
                          <th style={{ padding: '0.5rem', textAlign: 'left' }}>{groupLabel}</th>
                          <th style={{ padding: '0.5rem', textAlign: 'right' }}>{groupBy === 'device' ? 'Magnitudes' : 'Puntos de medición'}</th>
                          <th style={{ padding: '0.5rem', textAlign: 'right' }}>Valor (USD)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {groupList.length === 0 && (
                          <tr><td colSpan="3" style={{ padding: '0.75rem', textAlign: 'center', color: '#9ca3af' }}>{emptyMsg}</td></tr>
                        )}
                        {groupList.map(g => (
                          <tr key={g._id} style={{ borderTop: '1px solid #e5e7eb' }}>
                            <td style={{ padding: '0.5rem' }}>
                              {g.name}
                              {groupBy === 'device' && <span style={{ color: '#9ca3af', marginLeft: '0.4rem', fontSize: '0.75rem' }}>({g.deviceId})</span>}
                            </td>
                            <td style={{ padding: '0.5rem', textAlign: 'right' }}>{g.measurementPoints}</td>
                            <td style={{ padding: '0.5rem', textAlign: 'right' }}>
                              <input
                                type="number" step="0.01" min="0"
                                value={siteValues[g._id] ?? ''}
                                onChange={e => setSiteValues({ ...siteValues, [g._id]: e.target.value })}
                                style={{ width: '110px', padding: '0.3rem', border: '1px solid #d1d5db', borderRadius: '4px', textAlign: 'right' }}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })()}

            {reportType === 'full' && (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.5rem' }}>
                  <Field label="Cotización USD (ARS)" type="number" value={usdRate} onChange={setUsdRate} placeholder="Ej: 1050.00" />
                  <Field label="IVA %" type="number" value={ivaRate} onChange={setIvaRate} />
                </div>

                <div style={{ marginBottom: '0.75rem' }}>
                  <Field label={`${groupBy === 'sector' ? 'Sectores' : groupBy === 'device' ? 'Dispositivos' : 'Sitios'} facturados (rellenar manualmente)`} type="number" value={sitesBilled} onChange={setSitesBilled} placeholder="Cantidad a mostrar en el informe" />
                </div>

                <div style={{ background: '#f0f9ff', borderRadius: '8px', padding: '0.75rem 1rem', fontSize: '0.85rem', marginBottom: '0.75rem', border: '1px solid #bae6fd' }}>
                  <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#0c4a6e', letterSpacing: '0.5px', marginBottom: '0.4rem' }}>EN DÓLARES</div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700 }}>
                    <span>Subtotal USD</span><span>USD {formatARS(subtotalUSD)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.5rem', paddingTop: '0.4rem', borderTop: '1px solid #bae6fd', color: '#92400e' }}>
                    <span>× cotización</span><span>$ {formatARS(usd)} / USD</span>
                  </div>
                  <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#0c4a6e', letterSpacing: '0.5px', marginTop: '0.6rem', marginBottom: '0.2rem' }}>EN PESOS</div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Subtotal</span><span>$ {formatARS(subtotalARS)}</span></div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>IVA {ivaRate}%</span><span>$ {formatARS(ivaARS)}</span></div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.4rem', paddingTop: '0.4rem', borderTop: '2px solid #0c4a6e', fontWeight: 'bold', color: '#0c4a6e', fontSize: '1.05rem' }}>
                    <span>TOTAL ARS</span><span>$ {formatARS(totalARS)}</span>
                  </div>
                </div>
              </>
            )}

            {reportType === 'full' ? (
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.6rem 0.8rem', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '6px', marginBottom: '0.75rem', cursor: 'pointer' }}>
                <input type="checkbox" checked={includeExecSummary} onChange={e => setIncludeExecSummary(e.target.checked)} />
                <span style={{ fontSize: '0.85rem', color: '#0c4a6e' }}>
                  <b>Incluir resumen ejecutivo</b> — agrega hojas adicionales con disponibilidad por dispositivo, alertas detectadas y bitácora del mes (puede tardar 5-15s en calcularse).
                </span>
              </label>
            ) : (
              <div style={{ padding: '0.6rem 0.8rem', background: '#ecfdf5', border: '1px solid #6ee7b7', borderRadius: '6px', marginBottom: '0.75rem', fontSize: '0.85rem', color: '#065f46' }}>
                <b>📊 Solo informe técnico</b> — el PDF incluirá únicamente las hojas del resumen ejecutivo (KPIs, estado por {groupBy === 'sector' ? 'sector' : groupBy === 'device' ? 'dispositivo' : 'sitio'}, magnitudes monitoreadas y bitácora). Sin hoja de facturación.
              </div>
            )}

            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.6rem 0.8rem', background: '#faf5ff', border: '1px solid #e9d5ff', borderRadius: includeCharts ? '6px 6px 0 0' : '6px', marginBottom: includeCharts ? 0 : '0.75rem', cursor: 'pointer' }}>
              <input type="checkbox" checked={includeCharts} onChange={e => setIncludeCharts(e.target.checked)} />
              <span style={{ fontSize: '0.85rem', color: '#6b21a8' }}>
                <b>📈 Incluir gráficos de historial</b> — agrega una hoja con un gráfico separado por cada magnitud que elijas, entre las fechas del período.
              </span>
            </label>

            {includeCharts && (
              <div style={{ border: '1px solid #e9d5ff', borderTop: 'none', borderRadius: '0 0 6px 6px', padding: '0.6rem 0.8rem', marginBottom: '0.75rem', maxHeight: '220px', overflowY: 'auto', background: '#fdfaff' }}>
                {(reportData.devices || []).filter(d => (d.magnitudes || []).length > 0).length === 0 ? (
                  <div style={{ fontSize: '0.8rem', color: '#94a3b8' }}>No hay magnitudes graficables en los dispositivos de este cliente.</div>
                ) : (
                  (reportData.devices || []).filter(d => (d.magnitudes || []).length > 0).map(d => (
                    <div key={d._id} style={{ marginBottom: '0.5rem' }}>
                      <div style={{ fontWeight: 600, fontSize: '0.82rem', color: '#334155', marginBottom: '0.2rem' }}>{d.name}</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem 0.9rem', paddingLeft: '0.5rem' }}>
                        {d.magnitudes.map(m => (
                          <label key={m.name} style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.8rem', color: '#475569', cursor: 'pointer' }}>
                            <input type="checkbox" checked={(chartSelections[d._id] || []).includes(m.name)} onChange={() => toggleChartMag(d._id, m.name)} />
                            {m.label}{m.unit ? ` (${m.unit})` : ''}{m.valueType === 'boolean' ? ' · ON/OFF' : ''}
                          </label>
                        ))}
                      </div>
                    </div>
                  ))
                )}
                <div style={{ fontSize: '0.72rem', color: '#a78bfa', marginTop: '0.3rem' }}>
                  {Object.values(chartSelections).reduce((s, arr) => s + arr.length, 0)} magnitud(es) seleccionada(s).
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button onClick={onClose} style={btnSecondary} disabled={generating}>Cancelar</button>
              <button onClick={handleGenerate} style={{ ...btnPrimary, opacity: generating ? 0.6 : 1 }} disabled={reportData.sites.length === 0 || generating}>
                {generating ? '⏳ Generando...' : '🖨️ Generar PDF'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const modalBackdrop = {
  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
  background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center',
  zIndex: 1000
};
const modalBox = {
  background: 'white', borderRadius: '12px', padding: '1.5rem', width: '90%',
  maxHeight: '90vh', overflowY: 'auto'
};
const btnPrimary = {
  padding: '0.55rem 1.1rem', background: '#0ea5e9', color: 'white', border: 'none',
  borderRadius: '6px', cursor: 'pointer', fontSize: '0.875rem', fontWeight: 600
};
const btnSecondary = {
  padding: '0.55rem 1.1rem', background: '#f3f4f6', color: '#1f2937', border: '1px solid #d1d5db',
  borderRadius: '6px', cursor: 'pointer', fontSize: '0.875rem'
};

export default ClientsManagement;
